import { useId, useState } from "react";
import type { RosterRow } from "../../data/roster";
import type { AssignableMember } from "../../data/schedule-assign";
import "./staff-week.css";

export interface StaffWeekGridProps {
  members: AssignableMember[];
  assignments: RosterRow[];
  days: string[];
  timeZone: string;
  selectedDate: string;
  onBrowseDate: (date: string) => void;
  onSelectCell: (member: AssignableMember, date: string) => void;
  onSelectAssignment: (member: AssignableMember, date: string, assignment: RosterRow) => void;
}

function memberName(member: AssignableMember): string {
  return member.guardDisplayName ?? member.guardLabel;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.map((part) => Array.from(part)[0] ?? "").filter(Boolean).filter((_, index) => index === 0 || index === parts.length - 1).join("").toLocaleUpperCase();
}

// Day headings are calendar labels, not instants. Only assignment timestamps are zoned.
function dateLabel(date: string, options: Intl.DateTimeFormatOptions): string {
  const anchor = new Date(`${date}T12:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(anchor.getTime())) return date;
  return new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" }).format(anchor);
}

function fullDate(date: string): string {
  return dateLabel(date, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function assignmentDate(iso: string | null, formatter: Intl.DateTimeFormat): string | null {
  if (iso === null || !Number.isFinite(Date.parse(iso))) return null;
  const parts = formatter.formatToParts(new Date(iso));
  return ["year", "month", "day"].map((part) => parts.find((value) => value.type === part)?.value ?? "").join("-");
}

function windowLabel(assignment: RosterRow, dateFormatter: Intl.DateTimeFormat, clock: Intl.DateTimeFormat): string {
  const startDate = assignmentDate(assignment.startsAt, dateFormatter);
  const endDate = assignmentDate(assignment.endsAt, dateFormatter);
  if (startDate === null || assignment.startsAt === null) return "Time unavailable";
  const start = clock.format(new Date(assignment.startsAt));
  if (endDate === null || assignment.endsAt === null) return `${start} · End unavailable`;
  const end = clock.format(new Date(assignment.endsAt));
  const daysLater = Math.round((Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86_400_000);
  return `${start} – ${end}${daysLater > 0 ? ` +${daysLater} day${daysLater === 1 ? "" : "s"}` : ""}`;
}

function propertyTone(assignment: RosterRow): number {
  const key = assignment.siteId ?? assignment.siteName ?? assignment.routeName ?? "";
  return Array.from(key).reduce((hash, char) => (hash * 31 + (char.codePointAt(0) ?? 0)) % 4, 0);
}

function assignmentStatus(status: string): string {
  return status.length > 0 ? status.replaceAll("_", " ") : "Status unavailable";
}

export function StaffWeekGrid({ members, assignments, days, timeZone, selectedDate, onBrowseDate, onSelectCell, onSelectAssignment }: StaffWeekGridProps) {
  const [search, setSearch] = useState("");
  const searchId = useId();
  const hintId = useId();
  const query = search.trim().toLocaleLowerCase();
  const visibleMembers = members.filter((member) => memberName(member).toLocaleLowerCase().includes(query));
  const dateFormatter = new Intl.DateTimeFormat("en-US", { year: "numeric", month: "2-digit", day: "2-digit", timeZone });
  const clock = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone });

  // Names are presentation only. Null or ambiguous references must never bind a shift to a person.
  const refCounts = new Map<string, number>();
  for (const member of members) {
    if (member.guardRef !== null && member.guardRef.length > 0) refCounts.set(member.guardRef, (refCounts.get(member.guardRef) ?? 0) + 1);
  }
  const byMember = new Map<string, Map<string, RosterRow[]>>();
  for (const assignment of assignments) {
    if (assignment.guardRef === null || refCounts.get(assignment.guardRef) !== 1) continue;
    const date = assignmentDate(assignment.startsAt, dateFormatter);
    if (date === null || !days.includes(date)) continue;
    let memberDays = byMember.get(assignment.guardRef);
    if (memberDays === undefined) {
      memberDays = new Map();
      byMember.set(assignment.guardRef, memberDays);
    }
    const cell = memberDays.get(date) ?? [];
    cell.push(assignment);
    memberDays.set(date, cell);
  }
  const visibleAssignmentCount = visibleMembers.reduce((sum, member) => sum + Array.from(byMember.get(member.guardRef ?? "")?.values() ?? []).reduce((count, rows) => count + rows.length, 0), 0);

  return (
    <section className="staff-week" aria-label="Staff week calendar">
      <div className="staff-week__context staff-week__sr-only">
        <p id={hintId}>Choose a person and day to schedule work.</p>
        <p>Times shown in <strong>{timeZone}</strong></p>
      </div>
      <div className="staff-week__scroll" tabIndex={0} role="region" aria-label="Staff schedule; scroll horizontally for all seven days" aria-describedby={hintId}>
        <table className="staff-week__table">
          <caption className="staff-week__sr-only">Staff assignments for the week{days[0] ? ` beginning ${fullDate(days[0])}` : ""}. Each row is a staff member; each column is a day.</caption>
          <thead>
            <tr>
              <th scope="col" className="staff-week__staff-heading">
                <label htmlFor={searchId} className="staff-week__sr-only">Search staff</label>
                <span className="staff-week__search-icon" aria-hidden="true">⌕</span>
                <input id={searchId} type="search" placeholder="Search staff" value={search} onChange={(event) => setSearch(event.target.value)} className="staff-week__search" />
              </th>
              {days.map((date) => (
                <th scope="col" key={date} className={date === selectedDate ? "staff-week__selected-day" : ""}>
                  <button type="button" className="staff-week__day" aria-label={`Show ${fullDate(date)}`} aria-pressed={date === selectedDate} onClick={() => onBrowseDate(date)}>
                    <span>{dateLabel(date, { weekday: "short" })}</span>
                    <strong>{dateLabel(date, { month: "short", day: "numeric" })}</strong>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleMembers.map((member) => {
              const name = memberName(member);
              const memberDays = byMember.get(member.guardRef ?? "");
              const count = Array.from(memberDays?.values() ?? []).reduce((sum, rows) => sum + rows.length, 0);
              return (
                <tr key={member.guardMembershipId}>
                  <th scope="row" className="staff-week__staff" aria-label={`${name}, ${member.role}`}>
                    <span className="staff-week__avatar" aria-hidden="true">{initials(name)}</span>
                    <span className="staff-week__person"><strong title={name}>{name}</strong><span>{count} assignment{count === 1 ? "" : "s"}</span></span>
                  </th>
                  {days.map((date) => {
                    const rows = [...(memberDays?.get(date) ?? [])].sort((a, b) => Date.parse(a.startsAt ?? "") - Date.parse(b.startsAt ?? ""));
                    return (
                      <td key={date} className={date === selectedDate ? "staff-week__selected-day" : ""}>
                        <div className={`staff-week__cell${rows.length > 0 ? " staff-week__cell--assigned" : ""}`}>
                          {rows.map((assignment) => {
                            const window = windowLabel(assignment, dateFormatter, clock);
                            const status = assignmentStatus(assignment.assignmentStatus);
                            const property = assignment.siteName ?? "Property unavailable";
                            return (
                              <button type="button" key={assignment.routeAssignmentId} className={`staff-week__shift staff-week__shift--tone-${propertyTone(assignment)}${assignment.assignmentStatus === "cancelled" ? " staff-week__shift--cancelled" : ""}`} aria-label={`View assignment: ${name}, ${property}, ${assignment.routeName ?? "Route unavailable"}, ${fullDate(date)}, ${window}, ${status}`} onClick={() => onSelectAssignment(member, date, assignment)}>
                                <strong className="staff-week__shift-time" title={window}>{window.replace(/\+(\d+) days?$/, "+$1")}</strong>
                                <span className="staff-week__shift-property" title={property}>{property}</span>
                                {assignment.routeName !== null ? <span className="staff-week__shift-route" title={assignment.routeName}>{assignment.routeName}</span> : null}
                                {assignment.assignmentStatus !== "scheduled" ? <span className="staff-week__shift-status">{status}</span> : null}
                              </button>
                            );
                          })}
                          <button type="button" className="staff-week__add" aria-label={`Schedule ${name} on ${fullDate(date)}`} onClick={() => onSelectCell(member, date)}><span aria-hidden="true">+</span><span className="staff-week__add-copy">Add shift</span></button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {visibleMembers.length === 0 ? <tr><td colSpan={days.length + 1} className="staff-week__empty">{members.length === 0 ? "No assignable staff loaded." : "No staff match your search."}</td></tr> : null}
          </tbody>
        </table>
      </div>
      <div className="staff-week__summary" role="status"><strong>Week overview</strong><span>{visibleMembers.length} staff shown</span><span>{visibleAssignmentCount} assignment{visibleAssignmentCount === 1 ? "" : "s"} shown</span><span className="staff-week__scroll-hint">Swipe or scroll to see every day</span></div>
      {days.length === 0 ? <p className="staff-week__no-dates">No calendar dates loaded.</p> : null}
    </section>
  );
}
